/**
 * usePermissions Hook
 * 
 * React hook to access the centralized permission service.
 * Use this throughout the application instead of hardcoded permission checks.
 * 
 * @module usePermissions
 */

import { useMemo } from 'react';
import useAuth from './useAuth';
import { createPermissionService } from '../utility/permissionService';

/**
 * Hook to access permission checking throughout the app
 * 
 * @returns {PermissionService} Permission service instance with methods:
 *   - isFreeUser()
 *   - isPremiumUser()
 *   - isEliteUser()
 *   - isPro2User()
 *   - canAccessFeature(featureName)
 *   - canAccessStockInstruments()
 *   - canAccessOrderflowIndex()
 *   - getDataFeedType()
 *   - hasResource(resourceName)
 *   - getUpgradeMessage(feature)
 *   - and more...
 * 
 * @example
 * ```javascript
 * import { usePermissions } from '@/common/hooks/usePermissions';
 * import { FEATURES } from '@/common/utility/featurePermissions';
 * 
 * function MyComponent() {
 *   const permissions = usePermissions();
 *   
 *   if (!permissions.canAccessFeature(FEATURES.NTM_VOLX_CHARTS)) {
 *     return <UpgradePrompt />;
 *   }
 *   
 *   return <Chart />;
 * }
 * ```
 */
export const usePermissions = () => {
  const { auth } = useAuth();
  
  // Only recreate permission service when auth data changes
  const permissions = useMemo(() => {
    return createPermissionService(auth);
  }, [auth?.role, auth?.resources, auth?.activePlan]);
  
  return permissions;
};

export default usePermissions;

